import React, { useCallback, useLayoutEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import { editorToolbarPluginKey } from '@atlaskit/editor-plugin-toolbar/plugin-key';
import { ToolbarButton } from '@atlaskit/editor-toolbar/toolbar-button';
import { ToolbarTooltip } from '@atlaskit/editor-toolbar/toolbar-tooltip';
import AiGenerativeRemixIcon from '@atlaskit/icon-lab/core/ai-generative-remix';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { startRemixCreateExperience } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-create-collect';
import { getRemixFeatureArea } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-feature-area';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import type { RemixContentComponentDefinition } from '@atlassian/malleable-ui-components/types';

import type { MalleableUiPlugin, RemixDisabledReason } from '../../malleableUiPluginType';
import { useIsSelectionUnremixable } from '../hooks/use-is-selection-unremixable';
import { messages } from '../messages';
import { getRemixDisabledTooltip } from '../remix-disabled-tooltip';
import {
	getEditorRemixLifecyclePresentation,
	subscribeToEditorRemixLifecyclePresentation,
} from '../utils/editor-remix-callback-registry';
import { hasAiEditInProgressState } from '../utils/is-ai-edit-in-progress';

const testId = 'editor-toolbar__remix-button';

interface RemixButtonProps {
	api: ExtractInjectionAPI<MalleableUiPlugin>;
	isDisabled?: boolean;
	isRemixHidden?: boolean;
	isSelectionRemixable?: RemixContentComponentDefinition['isSelectionRemixable'];
	remixDisabledReason?: RemixDisabledReason;
}

export const RemixButton = ({
	api,
	isDisabled,
	isRemixHidden,
	isSelectionRemixable,
	remixDisabledReason,
}: RemixButtonProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const experienceTracker = useAIMateExperienceTracker();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { isLegacyRemixing, isAnyRemixing, isUpdate, aiEditInProgress, areAiEntryPointsDisabled } =
		useSharedPluginStateWithSelector(api, ['malleableUi', 'aiExperience'], (states) => ({
			isLegacyRemixing:
				states.malleableUiState?.isRemixModalOpen || states.malleableUiState?.isThinking || false,
			// Only the editor treatment projects this, from the shared Remix registry.
			isAnyRemixing: states.malleableUiState?.isAnyRemixing ?? false,
			isUpdate: states.malleableUiState?.isUpdate ?? false,
			areAiEntryPointsDisabled: states.aiExperienceState?.areAiEntryPointsDisabled ?? false,
			aiEditInProgress: hasAiEditInProgressState(states.aiExperienceState),
		}));

	// Ownership lives in a synchronous registry rather than plugin state, so subscribe to keep the
	// button's disabled state and tooltip in step with the flow starting and ending.
	const [, refreshMachinePresentation] = useState(0);
	useLayoutEffect(
		() =>
			subscribeToEditorRemixLifecyclePresentation(api, () =>
				refreshMachinePresentation((value) => value + 1),
			),
		[api],
	);
	// A machine-owned flow is a Remix in progress even though the projection reports `!isThinking`
	// once the early streaming whiteboard lands. Without this the button reads as disabled with no
	// reason, and the tooltip helper's default renders the archived copy on an ordinary page.
	// Only `inserting` extends "in progress". Ownership stays active after the flow completes, so
	// keying off it would leave this button disabled for the rest of the editor session.
	const machinePresentation = getEditorRemixLifecyclePresentation(api);
	const isOwnRemixing = isLegacyRemixing || machinePresentation.phase === 'inserting';
	// A Remix from another surface (e.g. the Object Sidebar, including one still generating in
	// Rovo Chat after its handoff) is also in progress, and the machine would refuse a new start.
	const isRemixing = isOwnRemixing || isAnyRemixing;
	// Active lifecycle authority is the treatment-owned gate, so the toolbar need not re-evaluate
	// the editor experiment while preserving legacy and Batch 5 classification paths.
	const isMachineOwnedEditInProgress =
		machinePresentation.authority === 'active' && isUpdate && isOwnRemixing;

	const effectiveDisabled = isDisabled || areAiEntryPointsDisabled || aiEditInProgress;
	const isRemixEnabled = !isRemixHidden && expValEquals('cc-maui-experiment', 'isEnabled', true);
	const isSelectionUnremixable = useIsSelectionUnremixable({
		api,
		isSelectionRemixable,
		isSurfaceAvailable: isRemixEnabled && !effectiveDisabled && !isRemixing,
	});

	const openRemix = useCallback(() => {
		const openModal = api?.malleableUi.commands.openRemixModal({
			metadata: {
				interactionSource: 'remixModalFromToolbar',
			},
			source: 'selectionToolbar',
		});
		if (!openModal) {
			return;
		}
		const featureArea = getRemixFeatureArea({});
		experienceTracker.start({
			name: ExperienceName.REMIX_START,
			id: ExperienceName.REMIX_START,
			attributes: {
				entryPoint: 'editor',
				remixSource: 'selectionToolbar',
				product: 'confluence',
				...(featureArea !== undefined && { featureArea }),
			},
		});
		startRemixCreateExperience({
			experienceTracker,
			attributes: {
				entryPoint: 'editor',
				remixSource: 'selectionToolbar',
				product: 'confluence',
			},
		});
		api?.core.actions.execute(({ tr }) => {
			openModal({ tr });
			tr.setMeta(editorToolbarPluginKey, { shouldShowToolbar: false });
			return tr;
		});
	}, [api, experienceTracker]);

	const handleClick = useCallback(() => {
		if (effectiveDisabled || isRemixing) {
			return;
		}
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'remixModalFromToolbar',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'remix',
				attributes: {
					entryPoint: 'editor',
				},
			},
		}).fire();

		openRemix();
	}, [createAnalyticsEvent, effectiveDisabled, isRemixing, openRemix]);

	if (!isRemixEnabled || isSelectionUnremixable) {
		return null;
	}

	let tooltipContent: string;
	if (isMachineOwnedEditInProgress || aiEditInProgress) {
		tooltipContent = formatMessage(messages.editInProgressTooltip);
	} else if (isRemixing) {
		tooltipContent = formatMessage(messages.remixInProgressTooltip);
	} else if (effectiveDisabled) {
		tooltipContent = getRemixDisabledTooltip(formatMessage, remixDisabledReason);
	} else if (fg('cc_maui_updated_remix_tooltip_text')) {
		tooltipContent = formatMessage(messages.remixToolbarTooltipUpdated);
	} else {
		tooltipContent = formatMessage(messages.remixToolbarTooltip);
	}

	const renderButton = () => (
		<ToolbarTooltip content={tooltipContent}>
			<ToolbarButton
				iconBefore={<AiGenerativeRemixIcon label="" size="small" />}
				testId={testId}
				onClick={handleClick}
				isDisabled={effectiveDisabled || isRemixing}
			>
				{formatMessage(messages.remixToolbarLabel)}
			</ToolbarButton>
		</ToolbarTooltip>
	);

	return renderButton();
};
